import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { listBranches, listUsers } from "../../api/directory";
import { listDepartments } from "../../api/departments";
import { previewNotice, sendNotice } from "../../api/companyNotify";
import { hasArabic, hasLatin } from "../../utils/lang";
import { useAutoTranslate } from "./useAutoTranslate";

const EMPTY = { title_ar: "", title_en: "", body_ar: "", body_en: "" };
const EMPTYF = { branch: "", department: "", employee: "" };
const clean = (key, v) =>
  key.endsWith("_ar") ? v.replace(/[A-Za-z]/g, "") : v.replace(/[\u0600-\u06FF]/g, "");

export const useComposer = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(EMPTYF);
  const [f, setF] = useState(EMPTY);
  const [channels, setChannels] = useState({ push: true, email: false });
  const [lists, setLists] = useState({ branches: [], departments: [], employees: [] });
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const { translating, autofill } = useAutoTranslate(setF);

  useEffect(() => {
    (async () => {
      const [b, d, e] = await Promise.all([listBranches(), listDepartments(), listUsers()]);
      setLists({ branches: b || [], departments: d || [], employees: e || [] });
    })();
  }, []);

  const setField = (key, value) => {
    const v = clean(key, value);
    setF((p) => ({ ...p, [key]: v }));
    setCount(null);
    autofill(key, v);
  };

  const updateFilters = (next) => {
    setFilters(next);
    setCount(null);
  };

  const mix = hasLatin(f.title_ar) || hasLatin(f.body_ar) || hasArabic(f.title_en) || hasArabic(f.body_en);
  const target = useMemo(() => {
    if (filters.employee) return { scope: "employee", id: filters.employee };
    if (filters.department) return { scope: "department", id: filters.department };
    if (filters.branch) return { scope: "branch", id: filters.branch };
    return { scope: "all", id: null };
  }, [filters]);

  const doPreview = async () => {
    try {
      setCount((await previewNotice(target)).recipients);
    } catch {
      setCount(null);
    }
  };

  const send = async () => {
    if (!(f.title_ar.trim() || f.title_en.trim()) || !(f.body_ar.trim() || f.body_en.trim()))
      return toast.error(t("notify.needText"));
    setBusy(true);
    try {
      const r = await sendNotice({ target, ...f, channels });
      if (!r.recipients) return toast.error(t("notify.noRecipients"));
      toast.success(t("notify.sent", { n: r.recipients }));
      if (r.push && !r.push.sent) toast(t("notify.pushNote"), { duration: 9000 });
      window.dispatchEvent(new Event("notify:sent"));
      setF(EMPTY);
      setCount(null);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(t(`errors.${code}`, { defaultValue: t("errors.generic") }));
    } finally {
      setBusy(false);
    }
  };

  return { filters, setFilters: updateFilters, f, setField, mix, translating, channels, setChannels, lists, count, doPreview, send, busy };
};
